import { useState, useEffect, useCallback, useMemo, useRef } from "react";

interface UseEntitySearchConfig<T extends { id: string }> {
  entities: Map<string, T>;
  fetchEntities: (
    params?: Record<string, string | undefined>,
  ) => Promise<string[]>;
  searchDebounceMs?: number;
}

/**
 * Hook for managing entity list display with server-side search and debouncing.
 *
 * **⚠️ For CONTROLLERS only (single instance per page), NOT for SELECTORS.**
 *
 * Use for: ItemsController, CompaniesController, etc. (one per page)
 * Don't use for: ItemSelector, CompanySelector, etc. (multiple per page - they need independent local state)
 *
 * Provides automatic initial fetch, debounced search, and optimistic UI updates.
 */
export function useEntitySearch<T extends { id: string }>({
  entities,
  fetchEntities,
  searchDebounceMs = 300,
}: UseEntitySearchConfig<T>) {
  const [displayedEntityIds, setDisplayedEntityIds] = useState<string[]>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchEntitiesRef = useRef(fetchEntities);
  const hasInitiallyFetchedRef = useRef(false);

  useEffect(() => {
    fetchEntitiesRef.current = fetchEntities;
  }, [fetchEntities]);

  useEffect(() => {
    if (!hasInitiallyFetchedRef.current) {
      hasInitiallyFetchedRef.current = true;
      fetchEntitiesRef.current().then((ids) => setDisplayedEntityIds(ids));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const entitiesArray = useMemo(() => {
    if (displayedEntityIds.length === 0) {
      return [];
    }
    return displayedEntityIds
      .map((id) => entities.get(id))
      .filter((entity): entity is T => entity !== undefined);
  }, [entities, displayedEntityIds]);

  const handleSearch = useCallback(
    (value: string, additionalParams?: Record<string, string | undefined>) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      const trimmedValue = value.trim();
      searchTimeoutRef.current = setTimeout(() => {
        const params = {
          ...(trimmedValue && { search: trimmedValue }),
          ...additionalParams,
        };
        fetchEntitiesRef
          .current(Object.keys(params).length > 0 ? params : undefined)
          .then((ids) => setDisplayedEntityIds(ids));
      }, searchDebounceMs);
    },
    [searchDebounceMs],
  );

  return {
    displayedEntityIds,
    setDisplayedEntityIds,
    entitiesArray,
    handleSearch,
  };
}
